// @ts-nocheck -- inherited from lift-teams; pre-existing type issues outside this port's scope
import { Toastify } from 'lift-ui';
import { useState, useEffect, useRef } from 'react';

import { generateUUID } from '@lift-admin/utils';
import { Brand, BrandConfig, BrandThreshold } from '../types';
import { dmgAlertApiService } from '../utils/dmgAlertApiService';

interface UseBrandManagementProps {
  brands: Brand[];
  selectedBrands: BrandConfig[];
  setSelectedBrands: React.Dispatch<React.SetStateAction<BrandConfig[]>>;
  setFormData: React.Dispatch<React.SetStateAction<import('../types').AlertFormData>>;
  defaultOperator?: '<' | '>' | '<=' | '>=' | '==' | '!=';
  defaultValue?: number;
  alert?: import('../types').Alert | null; // For initializing from backend data
  alertTypeName?: string; // Alert type name (e.g., 'emq', 'pacing', 'taxonomy')
}

export const useBrandManagement = ({
  brands,
  selectedBrands,
  setSelectedBrands,
  setFormData,
  defaultOperator,
  defaultValue,
  alert,
  alertTypeName,
}: UseBrandManagementProps) => {
  type PixelType = Array<{
    id: number;
    brand_id: number;
    account_id: string;
    pixel_id: string;
    pixel_name: string;
    created_at: string;
    updated_at: string;
  }>;

  type EventsByPixelIdType = Record<
    number,
    Array<{
      id: number;
      pixel_id_fk: number;
      event_name: string;
      composite_score: number | null;
      snapshot_date: string;
      created_at: string;
    }>
  >;

  // Initialize pixels from backend data if editing
  const initializePixelsFromBackend = (): PixelType => {
    if (!alert?.brands) return [];

    const pixelMap = new Map<string, PixelType[0]>();

    // Create synthetic pixel objects from backend threshold_config data
    alert.brands.forEach(brand => {
      brand.threshold_config?.forEach(threshold => {
        if (threshold.pixel_id && threshold.pixel_name && !pixelMap.has(threshold.pixel_id)) {
          // Use a temporary ID based on pixel_id hash (will be replaced by real API data)
          const tempId = Math.abs(
            threshold.pixel_id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
          );
          pixelMap.set(threshold.pixel_id, {
            id: tempId,
            brand_id: brand.brand_id,
            account_id: '',
            pixel_id: threshold.pixel_id,
            pixel_name: threshold.pixel_name,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        }
      });
    });

    return Array.from(pixelMap.values());
  };

  // Initialize events from backend data if editing
  const initializeEventsFromBackend = (initialPixels: PixelType): EventsByPixelIdType => {
    if (!alert?.brands) {
      return {};
    }

    const eventsMap: EventsByPixelIdType = {};

    // Create synthetic event objects from backend threshold_config data
    alert.brands.forEach(brand => {
      brand.threshold_config?.forEach(threshold => {
        if (threshold.pixel_id && threshold.event_name) {
          // Find the pixel to get its ID
          const pixel = initialPixels.find(p => p.pixel_id === threshold.pixel_id);
          if (pixel) {
            if (!eventsMap[pixel.id]) {
              eventsMap[pixel.id] = [];
            }
            // Only add if not already in the array
            const exists = eventsMap[pixel.id].some(e => e.event_name === threshold.event_name);
            if (!exists) {
              eventsMap[pixel.id].push({
                id: threshold.event_id || 0,
                pixel_id_fk: pixel.id,
                event_name: threshold.event_name,
                composite_score: null,
                snapshot_date: new Date().toISOString(),
                created_at: new Date().toISOString(),
              });
            }
          }
        }
      });
    });

    return eventsMap;
  };

  const initialPixels = initializePixelsFromBackend();
  const initialEvents = initializeEventsFromBackend(initialPixels);

  const [pixels, setPixels] = useState<PixelType>(initialPixels);

  // Store events per pixel ID to avoid conflicts between rows
  const [eventsByPixelId, setEventsByPixelId] = useState<EventsByPixelIdType>(initialEvents);

  // Track loading state for events fetching
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);

  // Track if we've already loaded pixels for the current brands (to avoid re-loading)
  const loadedBrandsRef = useRef<Set<number>>(new Set());

  const addBrand = async (brandName: string) => {
    const selectedBrand = brands.find(brand => brand.name === brandName);
    if (!selectedBrand) {
      return;
    }

    // Only check EMQ configuration for EMQ alerts, not for pacing or taxonomy
    const isEMQAlert = alertTypeName === 'emq';

    if (isEMQAlert && selectedBrand.isEmq === false) {
      Toastify(
        'warning',
        'EMQ is not configured for this brand. Please contact admin or submit a ticket to configure EMQ.'
      );
      return;
    }

    if (!selectedBrands.find(bc => bc.brandId === parseInt(selectedBrand.id))) {
      const brandIdNum = parseInt(selectedBrand.id);

      // Set default date range to current date
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
      const defaultEndDate = `${startOfDay.toISOString()}|${endOfDay.toISOString()}`;
      // Format today's date as MM/DD/YYYY for startDate
      const formattedToday = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}/${today.getFullYear()}`;

      // Add brand to state first
      const newBrandConfig: BrandConfig = {
        brandId: brandIdNum,
        brandName: selectedBrand.name,
        thresholds: [
          {
            id: generateUUID(),
            threshold: defaultValue ?? 0,
            operator: (defaultOperator as any) ?? '<',
            calendarType: 'relative',
            startDate: formattedToday,
            endDate: defaultEndDate,
          },
        ],
      };

      setSelectedBrands(prev => [...prev, newBrandConfig]);
      setFormData(prev => ({
        ...prev,
        brandConfigs: [...prev.brandConfigs, newBrandConfig],
      }));

      // Only fetch pixels for EMQ alerts
      if (isEMQAlert) {
        // Then check if pixels exist, and remove brand if there's an issue
        try {
          const pixelsData = await dmgAlertApiService.getPixels(brandIdNum);

          // Check if brand has no pixels
          if (!pixelsData || pixelsData.length === 0) {
            // Remove the brand we just added
            removeBrand(brandIdNum);
            Toastify(
              'warning',
              'Events are not configured for this brand. Please contact admin or submit a ticket to configure events.'
            );
            return;
          }

          // Pixels exist, add them to state
          setPixels(prev => {
            // Remove any existing pixels for this brand to avoid duplicates
            const filteredPixels = prev.filter(p => p.brand_id !== brandIdNum);
            return [...filteredPixels, ...pixelsData];
          });
          // Mark brand as loaded
          loadedBrandsRef.current.add(brandIdNum);
        } catch (error) {
          console.error('Error fetching pixels:', error);
          // Remove the brand we just added
          removeBrand(brandIdNum);
          // Show toast on error
          Toastify(
            'warning',
            'Events are not configured for this brand. Please contact admin or submit a ticket to configure events.'
          );
        }
      }
    }
  };

  const removeBrand = (brandId: number) => {
    setSelectedBrands(prev => prev.filter(bc => bc.brandId !== brandId));
    setFormData(prev => ({
      ...prev,
      brandConfigs: prev.brandConfigs.filter(bc => bc.brandId !== brandId),
    }));

    // Clean up pixels and events associated with this brand
    setPixels(prev => {
      const removedPixelIds = prev.filter(p => p.brand_id === brandId).map(p => p.id);

      // Remove events for those pixels
      setEventsByPixelId(prevEvents => {
        const newEvents = { ...prevEvents };
        removedPixelIds.forEach(pixelId => {
          delete newEvents[pixelId];
        });
        return newEvents;
      });

      return prev.filter(p => p.brand_id !== brandId);
    });

    // Remove from loaded brands tracking
    loadedBrandsRef.current.delete(brandId);
  };

  const addThresholdRow = (brandId: number) => {
    // Set default date range to current date
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
    const defaultEndDate = `${startOfDay.toISOString()}|${endOfDay.toISOString()}`;
    // Format today's date as MM/DD/YYYY for startDate
    const formattedToday = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}/${today.getFullYear()}`;

    const newThreshold: BrandThreshold = {
      id: generateUUID(),
      threshold: defaultValue ?? 0,
      operator: (defaultOperator as any) ?? '<',
      calendarType: 'relative', // Default to relative for new thresholds
      startDate: formattedToday, // Default to today's date
      endDate: defaultEndDate, // Current date as default
    };
    setSelectedBrands(prev =>
      prev.map(bc =>
        bc.brandId === brandId ? { ...bc, thresholds: [...bc.thresholds, newThreshold] } : bc
      )
    );
    setFormData(prev => ({
      ...prev,
      brandConfigs: prev.brandConfigs.map(bc =>
        bc.brandId === brandId ? { ...bc, thresholds: [...bc.thresholds, newThreshold] } : bc
      ),
    }));
  };

  const removeThresholdRow = (brandId: number, thresholdIndex: number) => {
    // Remove the threshold row
    // Note: We do NOT clean up events here because they should persist
    // for the filter dropdowns even when rows are deleted
    setSelectedBrands(prev =>
      prev.map(bc =>
        bc.brandId === brandId
          ? { ...bc, thresholds: bc.thresholds.filter((_, index) => index !== thresholdIndex) }
          : bc
      )
    );
    setFormData(prev => ({
      ...prev,
      brandConfigs: prev.brandConfigs.map(bc =>
        bc.brandId === brandId
          ? { ...bc, thresholds: bc.thresholds.filter((_, index) => index !== thresholdIndex) }
          : bc
      ),
    }));
  };

  const updateThreshold = (
    brandId: number,
    thresholdIndex: number,
    field: keyof BrandThreshold,
    value: string | number
  ) => {
    setSelectedBrands(prev =>
      prev.map(bc =>
        bc.brandId === brandId
          ? {
              ...bc,
              thresholds: bc.thresholds.map((threshold, index) =>
                index === thresholdIndex ? { ...threshold, [field]: value } : threshold
              ),
            }
          : bc
      )
    );
    setFormData(prev => ({
      ...prev,
      brandConfigs: prev.brandConfigs.map(bc =>
        bc.brandId === brandId
          ? {
              ...bc,
              thresholds: bc.thresholds.map((threshold, index) =>
                index === thresholdIndex ? { ...threshold, [field]: value } : threshold
              ),
            }
          : bc
      ),
    }));
  };

  // Update multiple thresholds at once (for synchronization)
  const updateMultipleThresholds = (
    brandId: number,
    updates: Array<{ thresholdIndex: number; field: keyof BrandThreshold; value: string | number }>
  ) => {
    console.log('[updateMultipleThresholds] Called', { brandId, updates });

    setSelectedBrands(prev => {
      console.log('[updateMultipleThresholds] Before update - selectedBrands', prev);
      
      const result = prev.map(bc => {
        if (bc.brandId !== brandId) return bc;

        const updatedThresholds = bc.thresholds.map((threshold, index) => {
          const update = updates.find(u => u.thresholdIndex === index);
          if (update) {
            console.log(`[updateMultipleThresholds] Updating threshold ${index}`, {
              before: threshold,
              field: update.field,
              value: update.value,
            });
          }
          return update ? { ...threshold, [update.field]: update.value } : threshold;
        });

        console.log('[updateMultipleThresholds] Updated thresholds', updatedThresholds);
        return { ...bc, thresholds: updatedThresholds };
      });
      
      console.log('[updateMultipleThresholds] After update - selectedBrands', result);
      return result;
    });
    
    setFormData(prev => ({
      ...prev,
      brandConfigs: prev.brandConfigs.map(bc => {
        if (bc.brandId !== brandId) return bc;

        const updatedThresholds = bc.thresholds.map((threshold, index) => {
          const update = updates.find(u => u.thresholdIndex === index);
          return update ? { ...threshold, [update.field]: update.value } : threshold;
        });

        return { ...bc, thresholds: updatedThresholds };
      }),
    }));
  };

  const updateLogicalOperator = (
    brandId: number,
    thresholdIndex: number,
    operator: 'AND' | 'OR' | 'NONE'
  ) => {
    updateThreshold(brandId, thresholdIndex, 'logicalOperator', operator);
  };

  // Reorder thresholds within a brand
  const reorderThresholds = (brandId: number, fromIndex: number, toIndex: number) => {
    setSelectedBrands(prev =>
      prev.map(bc => {
        if (bc.brandId === brandId) {
          const newThresholds = [...bc.thresholds];
          const [removed] = newThresholds.splice(fromIndex, 1);
          newThresholds.splice(toIndex, 0, removed);

          // Clear logical operator from the last threshold (it can't connect to anything below)
          const lastIndex = newThresholds.length - 1;
          if (
            newThresholds[lastIndex].logicalOperator &&
            newThresholds[lastIndex].logicalOperator !== 'NONE'
          ) {
            newThresholds[lastIndex] = {
              ...newThresholds[lastIndex],
              logicalOperator: 'NONE',
            };
          }

          return { ...bc, thresholds: newThresholds };
        }
        return bc;
      })
    );
  };

  const clearAllThresholds = (brandId: number) => {
    setSelectedBrands(prev =>
      prev.map(bc => {
        if (bc.brandId === brandId) {
          return { ...bc, thresholds: [] };
        }
        return bc;
      })
    );
    setFormData(prev => ({
      ...prev,
      brandConfigs: prev.brandConfigs.map(bc => {
        if (bc.brandId === brandId) {
          return { ...bc, thresholds: [] };
        }
        return bc;
      }),
    }));
  };

  const handlePixelSelection = async (brandId: number, thresholdIndex: number, pixelId: string) => {
    const selectedPixel = pixels.find(pixel => pixel.pixel_id === pixelId);
    if (selectedPixel) {
      updateThreshold(brandId, thresholdIndex, 'pixelId', selectedPixel.pixel_id);
      updateThreshold(brandId, thresholdIndex, 'pixelName', selectedPixel.pixel_name);

      // Fetch events for the selected pixel and store them by pixel ID
      // Only fetch if we don't already have events for this pixel
      if (!eventsByPixelId[selectedPixel.id]) {
        try {
          const eventsData = await dmgAlertApiService.getPixelEvents(selectedPixel.id.toString());
          setEventsByPixelId(prev => ({
            ...prev,
            [selectedPixel.id]: eventsData,
          }));
        } catch (error) {
          console.error('Error fetching events:', error);
          setEventsByPixelId(prev => ({
            ...prev,
            [selectedPixel.id]: [],
          }));
        }
      }
    }
  };

  // Helper function to get events for a specific threshold based on its selected pixel
  const getEventsForThreshold = (threshold: BrandThreshold) => {
    if (!threshold.pixelId) {
      return [];
    }

    // First, try to find the pixel by pixel_id string
    const pixel = pixels.find(p => p.pixel_id === threshold.pixelId);
    if (pixel && eventsByPixelId[pixel.id]) {
      return eventsByPixelId[pixel.id];
    }

    // Fallback: search through all events to find ones matching this pixel_id
    // This helps during loading when pixel IDs might not match yet
    for (const pixelIdKey in eventsByPixelId) {
      const pixelId = Number(pixelIdKey);
      const events = eventsByPixelId[pixelId];
      const eventPixel = pixels.find(p => p.id === pixelId);

      // Match by pixel_id string (more reliable than pixel.id number)
      if (eventPixel?.pixel_id === threshold.pixelId && events && events.length > 0) {
        return events;
      }
    }

    return [];
  };

  // Directly fetch events for a pixel without manipulating thresholds
  const fetchEventsForPixel = async (pixelId: string): Promise<void> => {
    const pixel = pixels.find(p => p.pixel_id === pixelId);
    if (!pixel) {
      console.warn(`Pixel ${pixelId} not found`);
      return;
    }

    // Skip if already have events
    if (eventsByPixelId[pixel.id]?.length > 0) {
      return;
    }

    try {
      const eventsData = await dmgAlertApiService.getPixelEvents(pixel.id.toString());
      setEventsByPixelId(prev => ({
        ...prev,
        [pixel.id]: eventsData,
      }));
    } catch (error) {
      console.error(`Error fetching events for pixel ${pixelId}:`, error);
    }
  };

  // Batch fetch events for multiple pixels
  const fetchEventsForPixels = async (pixelIds: string[]): Promise<void> => {
    const fetchPromises = pixelIds.map(pixelId => fetchEventsForPixel(pixelId));
    await Promise.all(fetchPromises);
  };

  // Load pixels and events for brands that already have thresholds (for edit mode)
  const loadPixelsAndEventsForBrands = async () => {
    setIsLoadingEvents(true);
    const brandIds = Array.from(new Set(selectedBrands.map(b => b.brandId)));

    // Load pixels for all brands
    const allPixels: PixelType = [];
    for (const brandId of brandIds) {
      try {
        const pixelsData = await dmgAlertApiService.getPixels(brandId);
        allPixels.push(...pixelsData);
      } catch (error) {
        console.error(`Error fetching pixels for brand ${brandId}:`, error);
      }
    }

    // Merge pixels: replace temporary ones with real API data, preserving events
    // Build pixel ID mapping first by comparing current pixels with new ones
    setPixels(prev => {
      const pixelMap = new Map<string, PixelType[0]>();
      const pixelIdMap = new Map<number, number>(); // Map old pixel.id -> new pixel.id

      // First, add all existing pixels (from backend init)
      prev.forEach(p => {
        pixelMap.set(p.pixel_id, p);
      });

      // Then, replace/update with real API pixels (matching by pixel_id)
      allPixels.forEach(p => {
        const oldPixel = prev.find(oldP => oldP.pixel_id === p.pixel_id);
        if (oldPixel && oldPixel.id !== p.id) {
          pixelIdMap.set(oldPixel.id, p.id);
        }
        pixelMap.set(p.pixel_id, p);
      });

      // Remap events to new pixel IDs if they changed
      if (pixelIdMap.size > 0) {
        setEventsByPixelId(prevEvents => {
          const remappedEvents: EventsByPixelIdType = {};

          // Copy all events, remapping pixel IDs
          for (const oldPixelId in prevEvents) {
            const newPixelId = pixelIdMap.get(Number(oldPixelId)) || Number(oldPixelId);
            // If remapping to a new ID, update the pixel_id_fk in the events as well
            if (pixelIdMap.has(Number(oldPixelId))) {
              remappedEvents[newPixelId] = (prevEvents[Number(oldPixelId)] || []).map(event => ({
                ...event,
                pixel_id_fk: newPixelId,
              }));
            } else {
              remappedEvents[newPixelId] = prevEvents[Number(oldPixelId)];
            }
          }

          return remappedEvents;
        });
      }

      return Array.from(pixelMap.values());
    });

    // Load events for pixels that are already selected in thresholds
    const pixelIds = new Set<string>();
    selectedBrands.forEach(brand => {
      brand.thresholds.forEach(threshold => {
        if (threshold.pixelId) {
          pixelIds.add(threshold.pixelId);
        }
      });
    });

    // Wait a bit for pixels state to update, then fetch events
    setTimeout(async () => {
      // Get the latest pixels and events from state to preserve existing data
      setEventsByPixelId(prevEvents => {
        const newEvents: EventsByPixelIdType = { ...prevEvents }; // Preserve existing events

        // Fetch events using the merged pixels
        const fetchEvents = async () => {
          setPixels(prevPixels => {
            const pixelMap = new Map<string, PixelType[0]>();
            prevPixels.forEach(p => pixelMap.set(p.pixel_id, p));
            allPixels.forEach(p => pixelMap.set(p.pixel_id, p));
            const updatedPixels = Array.from(pixelMap.values());

            // Fetch events for each pixel
            const eventPromises = Array.from(pixelIds).map(async pixelId => {
              const pixel = updatedPixels.find(p => p.pixel_id === pixelId);
              if (pixel) {
                try {
                  const eventsData = await dmgAlertApiService.getPixelEvents(pixel.id.toString());
                  // Merge with existing events, preferring API data but keeping backend data visible
                  const existingEvents = newEvents[pixel.id] || [];
                  const eventMap = new Map<string, (typeof eventsData)[0]>();

                  // Add existing events first (from backend init)
                  existingEvents.forEach(e => {
                    eventMap.set(e.event_name, e);
                  });

                  // Then add/update with API events (these have full data)
                  eventsData.forEach(e => {
                    eventMap.set(e.event_name, e);
                  });

                  newEvents[pixel.id] = Array.from(eventMap.values());
                } catch (error) {
                  console.error(`Error fetching events for pixel ${pixel.id}:`, error);
                  // Keep existing events if API call fails - don't clear them
                  if (!newEvents[pixel.id]) {
                    newEvents[pixel.id] = [];
                  }
                }
              }
            });

            Promise.all(eventPromises)
              .then(() => {
                setEventsByPixelId(newEvents);
                setIsLoadingEvents(false);
              })
              .catch(error => {
                console.error('[loadPixelsAndEventsForBrands] Error fetching events:', error);
                setIsLoadingEvents(false);
              });

            return updatedPixels;
          });
        };

        fetchEvents();

        return newEvents; // Return current events immediately, will be updated when API completes
      });
    }, 100);
  };

  // Refresh pixels and events for all selected brands
  const refreshPixelsAndEvents = async () => {
    setIsLoadingEvents(true);
    const brandIds = Array.from(new Set(selectedBrands.map(b => b.brandId)));

    // Save existing events to preserve them during refresh
    const existingEventsSnapshot = { ...eventsByPixelId };

    // Clear existing pixels (events will be preserved and merged back)
    setPixels([]);
    // Reset loaded brands tracking so they can be reloaded
    loadedBrandsRef.current.clear();

    // Load pixels for all brands
    const allPixels: typeof pixels = [];
    for (const brandId of brandIds) {
      try {
        const pixelsData = await dmgAlertApiService.getPixels(brandId);
        allPixels.push(...pixelsData);
        loadedBrandsRef.current.add(brandId);
      } catch (error) {
        console.error(`Error fetching pixels for brand ${brandId}:`, error);
      }
    }

    setPixels(allPixels);

    // Load events for all pixels (not just those in thresholds)
    // This ensures events are available even if pixels are only selected in filters
    const pixelIds = new Set<string>();

    // First, get pixels from thresholds
    selectedBrands.forEach(brand => {
      brand.thresholds.forEach(threshold => {
        if (threshold.pixelId) {
          pixelIds.add(threshold.pixelId);
        }
      });
    });

    // Also get all pixels for the brand to ensure all events are available
    allPixels.forEach(pixel => {
      pixelIds.add(pixel.pixel_id);
    });

    // Wait a moment for pixels state to update, then fetch events
    setTimeout(async () => {
      const newEvents: EventsByPixelIdType = { ...existingEventsSnapshot }; // Preserve existing events

      for (const pixelId of Array.from(pixelIds)) {
        const pixel = allPixels.find(p => p.pixel_id === pixelId);
        if (pixel) {
          try {
            const eventsData = await dmgAlertApiService.getPixelEvents(pixel.id.toString());
            // Merge with existing events if any
            const existingEvents = newEvents[pixel.id] || [];
            const eventMap = new Map<string, (typeof eventsData)[0]>();

            // Add existing events first
            existingEvents.forEach(e => {
              eventMap.set(e.event_name, e);
            });

            // Then add/update with API events
            eventsData.forEach(e => {
              eventMap.set(e.event_name, e);
            });

            newEvents[pixel.id] = Array.from(eventMap.values());
          } catch (error) {
            console.error(`Error fetching events for pixel ${pixel.id}:`, error);
            // Keep existing events if API call fails
            if (!newEvents[pixel.id]) {
              newEvents[pixel.id] = [];
            }
          }
        }
      }

      setEventsByPixelId(newEvents);
      setIsLoadingEvents(false);
    }, 300);
  };

  // Auto-load pixels and events when selectedBrands change (for edit mode)
  useEffect(() => {
    if (selectedBrands.length > 0) {
      const hasThresholdsWithPixels = selectedBrands.some(brand =>
        brand.thresholds.some(t => t.pixelId)
      );

      if (hasThresholdsWithPixels) {
        const brandIds = new Set(selectedBrands.map(b => b.brandId));
        const newBrandIds = Array.from(brandIds).filter(id => !loadedBrandsRef.current.has(id));

        if (newBrandIds.length > 0) {
          // Mark these brands as loaded
          newBrandIds.forEach(id => loadedBrandsRef.current.add(id));
          // Load pixels and events for brands with existing thresholds
          loadPixelsAndEventsForBrands();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrands.length]);

  return {
    pixels,
    eventsByPixelId,
    getEventsForThreshold,
    fetchEventsForPixel,
    fetchEventsForPixels,
    addBrand,
    removeBrand,
    addThresholdRow,
    removeThresholdRow,
    updateThreshold,
    updateMultipleThresholds,
    updateLogicalOperator,
    reorderThresholds,
    handlePixelSelection,
    refreshPixelsAndEvents,
    isLoadingEvents,
    clearAllThresholds,
  };
};